// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Inline equipment flow view — the alarm detail's "Asset Topology" panel.
 *
 * A compact, self-contained visualisation of one asset's power-flow
 * neighbourhood: equipment feeding *in* is shown above, equipment being *fed*
 * is shown below, at most two hops either way. Each level is capped (default 2)
 * and any remainder is shown as a "+N" overflow chip.
 *
 * Presentation follows the Helios Bindings design: full-width stacked nodes,
 * each a status dot plus a single-line label, joined by animated dashed flow
 * connectors, with the focused asset called out in the Helios accent.
 *
 * The component is host-agnostic — it reads the shared digital-twin server
 * state for the given (or active) site and renders nothing when the asset isn't
 * in the graph, so it is safe to drop into an alarm detail, a chat reply, or
 * anywhere else without a surrounding existence check. Direction and status are
 * derived in `buildEquipmentFlow`; this file is presentation only.
 */

import React, { useMemo } from 'react';
import { ActionableNotification, SkeletonText } from '@carbon/react';
import { useActiveSite } from '../../hooks/useActiveSite';
import { useDigitalTwin } from '../../hooks/useDigitalTwin';
import {
  buildEquipmentFlow,
  type EquipmentFlowLevel,
  type EquipmentNode,
} from '../../utils/equipmentFlowGraph';
import type { StatusToken } from '../../utils/entityHelpers';
import styles from './InlineEquipmentView.module.css';

export interface InlineEquipmentViewProps {
  /** Twin entity id of the asset to centre the view on. */
  equipmentId: string;
  /** OMS site id. Defaults to the active site when omitted. */
  siteId?: string;
  /** Hops to walk in each direction. Default 2. */
  maxDepth?: number;
  /** Nodes rendered per level before the overflow chip. Default 2. */
  maxPerLevel?: number;
  /** Optional click handler; when set, chips become buttons. */
  onSelectEquipment?: (_equipmentId: string) => void;
  /**
   * Optional heading rendered above the flow. Because the whole component
   * renders `null` when the asset isn't in the graph, a heading passed here
   * disappears with it — so a host card never shows an empty titled shell.
   */
  heading?: string;
  className?: string;
}

/** StatusToken → the Helios status colour token used for the node dot. */
const STATUS_VAR: Record<StatusToken, string> = {
  ok: 'var(--st-ok)',
  warn: 'var(--st-warn)',
  fault: 'var(--st-fault)',
  idle: 'var(--st-idle)',
};

/** Human-readable status for the dot's title/aria. */
const STATUS_LABEL: Record<StatusToken, string> = {
  ok: 'Monitoring',
  warn: 'Degraded',
  fault: 'Fault',
  idle: 'No data / standby',
};

interface NodeChipProps {
  node: EquipmentNode;
  focus?: boolean;
  onSelect?: (_equipmentId: string) => void;
}

const NodeChip = ({ node, focus, onSelect }: NodeChipProps) => {
  const content = (
    <>
      <span className={styles.dot} aria-hidden="true" />
      <span className={styles.labels}>
        <span className={styles.label}>{node.label}</span>
        {focus && <span className={styles.focusNote}>This asset</span>}
      </span>
    </>
  );

  const chipClass = `${styles.chip} ${focus ? styles.focus : ''}`.trim();
  // Drives the border, the dot, and (via the level) the connector below it. The
  // code/type badges the design drops still reach assistive tech and hover
  // through the label and title.
  const nodeStyle = { '--node-color': STATUS_VAR[node.status] } as React.CSSProperties;
  const describedAs = `${node.label}, ${node.typeLabel}, ${STATUS_LABEL[node.status]}`;

  // Focus is the asset already in view — never a navigation target.
  if (onSelect && !focus) {
    return (
      <button
        type="button"
        className={chipClass}
        style={nodeStyle}
        title={describedAs}
        onClick={() => onSelect(node.id)}
        aria-label={describedAs}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      className={chipClass}
      style={nodeStyle}
      title={describedAs}
      aria-current={focus ? 'true' : undefined}
      aria-label={describedAs}
    >
      {content}
    </div>
  );
};

interface LevelRowProps {
  level: EquipmentFlowLevel;
  onSelect?: (_equipmentId: string) => void;
}

const LevelRow = ({ level, onSelect }: LevelRowProps) => (
  <div className={styles.level}>
    {level.nodes.map(node => (
      <NodeChip key={node.id} node={node} onSelect={onSelect} />
    ))}
    {level.overflow > 0 && (
      <span
        className={styles.overflow}
        title={`${level.overflow} more not shown`}
        aria-label={`${level.overflow} more not shown`}
      >
        +{level.overflow}…
      </span>
    )}
  </div>
);

/**
 * Animated dashed line between two levels. Takes its colour from the node the
 * flow arrives at, so a fault downstream is traced by an amber/red run rather
 * than a neutral rule.
 */
const Connector = ({ status }: { status: StatusToken }) => (
  <div
    className={styles.connector}
    style={{ '--flow-color': STATUS_VAR[status] } as React.CSSProperties}
    data-testid="flow-connector"
    aria-hidden="true"
  />
);

/** The worst status in a level, so one faulting neighbour colours its connector. */
const STATUS_SEVERITY: Record<StatusToken, number> = { fault: 3, warn: 2, ok: 1, idle: 0 };
function levelStatus(level: EquipmentFlowLevel): StatusToken {
  return level.nodes.reduce<StatusToken>(
    (worst, n) => (STATUS_SEVERITY[n.status] > STATUS_SEVERITY[worst] ? n.status : worst),
    'idle'
  );
}

export default function InlineEquipmentView({
  equipmentId,
  siteId,
  maxDepth,
  maxPerLevel,
  onSelectEquipment,
  heading,
  className,
}: InlineEquipmentViewProps) {
  const { siteId: activeSiteId } = useActiveSite();
  const resolvedSiteId = siteId ?? activeSiteId;
  const { entities, relationships, loading, error, truncated, refetch } =
    useDigitalTwin(resolvedSiteId);

  const flow = useMemo(
    () => buildEquipmentFlow(equipmentId, entities, relationships, { maxDepth, maxPerLevel }),
    [equipmentId, entities, relationships, maxDepth, maxPerLevel]
  );

  const rootClass = `${styles.root} ${className ?? ''}`.trim();

  // Only show the skeleton on a genuine cold load. Once the graph is cached, an
  // unknown/absent asset should fall straight through to the empty render.
  if (loading && entities.length === 0) {
    return (
      <div className={rootClass} data-testid="inline-equipment-view">
        {heading && <div className={styles.heading}>{heading}</div>}
        <SkeletonText paragraph lineCount={3} width="80%" />
      </div>
    );
  }

  // A failed load with nothing cached, as Power Flow reports it: the empty
  // render below would otherwise pass for "this asset has no neighbours".
  if (error && entities.length === 0) {
    return (
      <div className={rootClass} data-testid="inline-equipment-view">
        {heading && <div className={styles.heading}>{heading}</div>}
        <ActionableNotification
          kind="error"
          lowContrast
          title="Could not load the power flow"
          subtitle={error}
          actionButtonLabel="Retry"
          onActionButtonClick={refetch}
          hideCloseButton
        />
      </div>
    );
  }

  // Asset not in the graph (or no id). When paging stopped early this could be
  // a real absence or just an unfetched page — the silent "no neighbours"
  // render would claim the former either way, so say so instead.
  if (!flow.focus) {
    if (!truncated) return null;
    return (
      <div className={rootClass} data-testid="inline-equipment-view">
        {heading && <div className={styles.heading}>{heading}</div>}
        <ActionableNotification
          kind="warning"
          lowContrast
          title="Power flow may be incomplete"
          subtitle="The site graph stopped loading before reaching this asset. It may still have neighbours."
          actionButtonLabel="Retry"
          onActionButtonClick={refetch}
          hideCloseButton
        />
      </div>
    );
  }

  // Farthest feeder at the top → focus → farthest load at the bottom.
  const upstreamTopDown = [...flow.upstream].reverse();

  return (
    <div
      className={rootClass}
      data-testid="inline-equipment-view"
      role="group"
      aria-label="Equipment power flow"
    >
      {heading && <div className={styles.heading}>{heading}</div>}

      {truncated && (
        <ActionableNotification
          kind="warning"
          lowContrast
          title="Power flow may be incomplete"
          subtitle="The site graph stopped loading early; some connections may be missing."
          actionButtonLabel="Retry"
          onActionButtonClick={refetch}
          hideCloseButton
        />
      )}

      {upstreamTopDown.map((level, i) => (
        <React.Fragment key={`up-${i}`}>
          <LevelRow level={level} onSelect={onSelectEquipment} />
          <Connector status={levelStatus(level)} />
        </React.Fragment>
      ))}

      <div className={styles.level}>
        <NodeChip node={flow.focus} focus />
      </div>

      {flow.downstream.map((level, i) => (
        <React.Fragment key={`down-${i}`}>
          <Connector status={levelStatus(level)} />
          <LevelRow level={level} onSelect={onSelectEquipment} />
        </React.Fragment>
      ))}
    </div>
  );
}
